import camelcase from "camelcase-keys";
import clsx from "clsx";
import { FunctionComponent } from "react";
import { Alert, ButtonToolbar, Col, Form, Row, Spinner } from "react-bootstrap";

import {
  Button,
  Icon,
  RenderWhen,
  TooltipIcon
} from "../../../../../components";
import Typography from "../../../../../components/typography/Typography";
import { FormLabel, form } from "../../../../../forms";
import FormCheckboxWidget from "../../../../../forms/components/FormCheckboxWidget";
import { WidgetPassword } from "../../../../../forms/components/WidgetPassword";
import { useSystemVersion } from "../../../../../hooks";
import { enums } from "../../../../../shim/jsbridge";
import { isLocaleAvailableForPlatform } from "../../../../../utils";
import {
  areAdditionalAnalysisTypesAvailable,
  getIsIntegrationEnabled
} from "../../../../header/hooks/usePrepareReanalyzeData";
import ReanalyzeCardContainer from "../../reanalyze/reanalyze-card-container/ReanalyzeCardContainer";
import ReanalyzeAuxiliaryFormInput from "../reanalyze-auxiliary-form-input/ReanalyzeAuxiliaryFormInput";
import ReanalyzeFormInput, {
  LAYOUT_TYPE
} from "../reanalyze-form-input/ReanalyzeFormInput";
import ReanalyzeIntegrationsFormInput from "../reanalyze-integrations-form-input/ReanalyzeIntegrationsFormInput";
import { INITIAL_RLCS_PARAMS } from "../reanalyze-popup/consts";
import InteractiveAnalysisInput, {
  InteractiveParamChangeValue
} from "../rlcs-configuration-input/InteractiveAnalysisInput";
import RlcsConfigurationInput, {
  RLCSParamChangeValue
} from "../rlcs-configuration-input/RlcsConfigurationInput";
import { DYNAMIC_INTEGRATIONS, UPLOAD_SOURCE_MESSAGES } from "./consts";
import styles from "./reanalyze-form.module.scss";
import { ReanalyzeFormProps } from "./types";

const ReanalyzeForm: FunctionComponent<ReanalyzeFormProps> = ({
  onSubmit = () => undefined,
  onCancel = () => undefined,
  onValues,
  popupWindowData,
  values,
  displayWarningMessage,
  onRlcsParamsChange,
  rlcsParams,
  rlcsErrors,
  interactiveErrors,
  interactiveParams,
  onInteractiveParamChange,
  tcAnalysisDisabled = false,
  ticloudAnalysisDisabled = false,
  submitButtonLabel = "Reanalyze",
  onFilePasswordChange,
  showFilePasswordField = true,
  interactiveAnalysisDisabled = false,
  isSubmitted = false,
  isLoading,
  showHint,
  showLocalOnlyAnalysis = false,
  modalLayout,
  sources,
  onSourcesChange,
  fileUrl
}) => {
  const { ticoreVersion } = useSystemVersion();
  const handleRlcsParamChange = (value: RLCSParamChangeValue) => {
    const platform = value?.platform ?? rlcsParams.platform;
    const locale = (value?.locale ?? rlcsParams.locale) as string;
    onRlcsParamsChange({
      ...value,
      ...([enums.platforms.MACOS_11, enums.platforms.ANDROID_12].includes(
        value
      ) && {
        internet_simulation: false
      }),
      ...(value === enums.platforms.ANDROID_12 && {
        execution_timeout: undefined
      }),
      ...(isLocaleAvailableForPlatform(platform)
        ? { locale: locale ?? INITIAL_RLCS_PARAMS.locale }
        : { locale: undefined })
    });
  };

  const handleInteractiveParamChange = (value: InteractiveParamChangeValue) => {
    onInteractiveParamChange(value);
  };

  const { tc, tiCloud, dynamicAnalysis } = camelcase(popupWindowData ?? {});
  const {
    rlDynamicAnalysis,
    rlDynamicAnalysisInteractive,
    rlAuxiliaryAnalysis
  } = camelcase(dynamicAnalysis ?? {});

  const isAnyDynamicAnalysisSelected = Object.entries(values)
    .filter(([key]) => key !== "local_only")
    .map(([, value]) => value)
    .some(Boolean);
  const isSubmitButtonDisabled = !isAnyDynamicAnalysisSelected || isLoading;

  const toggleValue = (fieldName: string) => {
    onValues?.({ ...values, [fieldName]: !values[fieldName] });
  };

  const tcDisabled = !tc?.enabled || tcAnalysisDisabled;
  const tcTooltipMessage = tcDisabled ? tc?.additional_information : undefined;

  const ticloudDisabled = !tiCloud?.enabled || ticloudAnalysisDisabled;
  const ticloudTooltipMessage = ticloudDisabled
    ? tiCloud?.additional_information
    : undefined;

  return (
    <Form onSubmit={onSubmit} className="reanalyze-form">
      <div>
        <RenderWhen condition={displayWarningMessage}>
          <div>
            <Alert
              variant="warning"
              className="mb-25"
              style={{ marginTop: "-25px" }}
            >
              <p>
                <b>Warning:</b> you are submitting several samples for
                reanalysis. Depending on their size and number, the appliance
                performance and the sample processing speed may be impacted.
              </p>
            </Alert>
          </div>
        </RenderWhen>

        <Typography variant="h5" className="mb-15">
          Local Analysis
        </Typography>
        <div
          className={clsx(
            "d-flex flex-wrap align-items-center",
            styles.container_gap
          )}
        >
          <div className="flex-grow-1">
            <ReanalyzeCardContainer
              disabled={tcDisabled}
              selected={values.tc}
              layout={LAYOUT_TYPE.ROW}
              tooltipMessage={
                tcAnalysisDisabled
                  ? "Static Analysis is mandatory when submitting a file."
                  : tcTooltipMessage
              }
              onClick={() => {
                if (tcDisabled) {
                  return;
                }
                toggleValue("tc");
              }}
              classNameOuterContainer={clsx(
                !showLocalOnlyAnalysis &&
                  modalLayout === "lg" &&
                  styles.static_analysis_analysis_container
              )}
            >
              <ReanalyzeFormInput
                name="tc"
                label="Static Analysis"
                labelIcon="static-analysis"
                labelIconSize={24}
                help={`Spectra Core v${ticoreVersion}`}
                timestamp={tc.last_completed_on}
                disabled={tcDisabled}
                layout={LAYOUT_TYPE.ROW}
                checked={values.tc}
              />
            </ReanalyzeCardContainer>
          </div>
          <RenderWhen condition={showLocalOnlyAnalysis}>
            <div
              className={clsx(
                "d-flex align-items-end",
                styles.integration_analysis_container
              )}
            >
              <FormCheckboxWidget
                name="local_only"
                label={
                  <FormLabel
                    label="Local Only Analysis"
                    tooltipText="File will be analyzed with Static Analysis only and other analysis types will be ignored (including automatic upload rules)."
                  />
                }
                skipColon
                marginBottom={0}
              />
            </div>
          </RenderWhen>
        </div>

        <hr className="my-25" />

        <div className={clsx("d-flex", styles.container_gap)}>
          <div className="flex-grow-1">
            <Typography variant="h5" className="mb-15">
              RL Analysis
            </Typography>

            <ReanalyzeCardContainer
              disabled={ticloudDisabled || values.local_only}
              selected={values.ti_cloud}
              layout={LAYOUT_TYPE.ROW}
              tooltipMessage={
                ticloudAnalysisDisabled
                  ? "File is automatically submitted to Threat Intelligence if RL Cloud Sandbox is selected."
                  : ticloudTooltipMessage
              }
              onClick={() => {
                if (ticloudDisabled || values.local_only) {
                  return;
                }
                toggleValue("ti_cloud");
              }}
              classNameOuterContainer="mb-10"
            >
              <ReanalyzeFormInput
                name="ti_cloud"
                label="Threat Intelligence"
                labelIcon="cloud-intelligence"
                labelIconSize={24}
                help={"Spectra Intelligence"}
                helpImportant={
                  ticloudDisabled ? undefined : tiCloud.additional_information
                }
                timestamp={tiCloud.last_completed_on}
                disabled={ticloudDisabled || values.local_only}
                layout={LAYOUT_TYPE.ROW}
                checked={values.ti_cloud}
              />
            </ReanalyzeCardContainer>

            <RlcsConfigurationInput
              isFileAnalysis
              isSelected={values.rl_dynamic_analysis}
              analysisInfo={rlDynamicAnalysis}
              params={rlcsParams}
              errors={rlcsErrors}
              disabled={values.local_only}
              onSelect={() => toggleValue("rl_dynamic_analysis")}
              onParamChange={handleRlcsParamChange}
            />

            <InteractiveAnalysisInput
              isFileAnalysis
              isSelected={values.rl_dynamic_analysis_interactive}
              analysisInfo={rlDynamicAnalysisInteractive}
              params={interactiveParams}
              errors={interactiveErrors}
              disabled={interactiveAnalysisDisabled || values.local_only}
              onSelect={() => toggleValue("rl_dynamic_analysis_interactive")}
              onParamChange={handleInteractiveParamChange}
            />

            <ReanalyzeAuxiliaryFormInput
              analysisInfo={rlAuxiliaryAnalysis}
              selected={values.rl_auxiliary_analysis}
              onToggle={toggleValue}
              disabled={values.local_only}
            />
          </div>

          <RenderWhen
            condition={Boolean(
              getIsIntegrationEnabled(popupWindowData?.dynamic_analysis)
            )}
          >
            <div className={styles.integration_analysis_container}>
              <Typography variant={"h5"} className={"mb-15"}>
                Integration Analysis
              </Typography>
              {DYNAMIC_INTEGRATIONS.map(({ name, label }) => (
                <ReanalyzeIntegrationsFormInput
                  key={name}
                  name={name}
                  label={label}
                  selected={values[name]}
                  onToggle={toggleValue}
                  analysisInfo={dynamicAnalysis[name]}
                  disabled={values.local_only}
                />
              ))}
            </div>
          </RenderWhen>
        </div>
      </div>

      {showFilePasswordField && (
        <>
          <hr className={"my-25"} />

          <div>
            <Typography variant={"h5"} className={"mb-15"}>
              Protected File
            </Typography>

            <WidgetPassword
              id="file_password"
              vertical
              name={"file_password"}
              label={
                <FormLabel
                  label={"File Password"}
                  tooltipText={
                    "Archive and password will be discarded after unpacking. Zip archives with 1 child file are supported."
                  }
                />
              }
              placeholder={"Enter Password"}
              onChange={e => onFilePasswordChange(e.currentTarget.value)}
              className={
                areAdditionalAnalysisTypesAvailable(popupWindowData)
                  ? styles.password_field_large
                  : styles.password_field_medium
              }
            />
          </div>
        </>
      )}

      {sources && onSourcesChange && (
        <>
          <hr className={"my-25"} />

          <div>
            <Typography variant={"h5"} className={"mb-15"}>
              Sources
            </Typography>

            <Form.Group className="mb-25" controlId="source-tag-input">
              <Form.Label className="d-flex align-items-center">
                Source Tag
                <TooltipIcon
                  icon="info-circle"
                  message={UPLOAD_SOURCE_MESSAGES.sourceTag}
                  className="ms-5"
                />
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Source Tag"
                spellCheck={false}
                onChange={e =>
                  onSourcesChange({
                    ...sources,
                    sourceTag: e.target.value
                  })
                }
                value={sources.sourceTag}
              />
            </Form.Group>

            <Form.Group className="mb-25" controlId="file-origin-link-input">
              <Form.Label className="d-flex align-items-center">
                Origin Link
                <TooltipIcon
                  icon="info-circle"
                  message={UPLOAD_SOURCE_MESSAGES.fileOriginLink}
                  className="ms-5"
                />
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Origin Link"
                spellCheck={false}
                disabled={Boolean(fileUrl)}
                onChange={e =>
                  onSourcesChange({
                    ...sources,
                    fileOriginLink: e.target.value
                  })
                }
                value={fileUrl || sources.fileOriginLink}
              />
            </Form.Group>
          </div>
        </>
      )}

      <RenderWhen condition={Boolean(showHint)}>
        <Row style={{ margin: "1rem 0" }}>
          <Col
            md={12}
            className="flex align-center flex-gap-1 line-height-normal opacity-75 p-0"
          >
            <Icon size={12} name="info-circle" />
            <Typography variant="body4" tag="span">
              Note: If only Static Analysis is selected, the sample will be
              analyzed following current analysis settings.
            </Typography>
          </Col>
        </Row>
      </RenderWhen>

      <Form.Group>
        <ButtonToolbar className="justify-content-end">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            data-testid="reanalyze_form_cancel_button"
            disabled={isSubmitted}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitButtonDisabled || isSubmitted}
            id="reanalyze-popup-submit-btn"
            data-testid="reanalyze_form_reanalyze_button"
          >
            {isSubmitted ? (
              <Spinner animation="border" size="sm" className="me-0" />
            ) : (
              submitButtonLabel
            )}
          </Button>
        </ButtonToolbar>
      </Form.Group>
    </Form>
  );
};

export default form(ReanalyzeForm);
